import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";
import { http } from "@/lib/api";
import { Loader2, ArrowLeft, Calendar } from "lucide-react";

/**
 * Tiny markdown-to-HTML converter that supports the subset used by our seed
 * posts (headings, bold, italic, links, unordered lists, tables, paragraphs).
 * A dedicated markdown lib would be overkill for 5-10 posts.
 */
function renderMarkdown(md) {
  if (!md) return "";
  let html = md.trim();
  // Escape HTML
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Tables (very basic — supports the |x|y|z| syntax used in the seed posts)
  html = html.replace(/((?:^\|.*\|\n)+)/gm, (block) => {
    const rows = block.trim().split("\n").filter(Boolean);
    if (rows.length < 2) return block;
    const header = rows[0].split("|").filter((c) => c.trim() !== "").map((c) => c.trim());
    const body = rows.slice(2).map((r) => r.split("|").filter((c) => c.trim() !== "").map((c) => c.trim()));
    return (
      '<div class="overflow-x-auto my-6"><table class="w-full text-sm border-collapse">' +
      "<thead><tr>" + header.map((h) => `<th class='border border-white/10 px-3 py-2 text-left text-white'>${h}</th>`).join("") + "</tr></thead>" +
      "<tbody>" + body.map((row) => "<tr>" + row.map((c) => `<td class='border border-white/10 px-3 py-2 text-[var(--fg-muted)]'>${c}</td>`).join("") + "</tr>").join("") + "</tbody>" +
      "</table></div>"
    );
  });
  // Headings — # is intentionally NOT converted to <h1> because each page
  // already has exactly one route-level H1. In-content H1s would break the
  // page's heading hierarchy for SEO.
  html = html
    .replace(/^### (.*)$/gm, '<h3 class="font-display text-xl text-white mt-8 mb-3">$1</h3>')
    .replace(/^## (.*)$/gm, '<h2 class="font-display text-2xl text-white mt-10 mb-4">$1</h2>')
    .replace(/^# (.*)$/gm, '<h2 class="font-display text-2xl text-white mt-10 mb-4">$1</h2>');
  // Bold + italic + inline code
  html = html
    .replace(/\*\*(.+?)\*\*/g, "<strong class='text-white'>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code class='px-1.5 py-0.5 bg-white/5 rounded text-cyan-200 text-sm'>$1</code>");
  // Links
  html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-cyan-300 underline hover:text-cyan-200">$1</a>');
  // Lists
  html = html.replace(/(^- .*(?:\n- .*)*)/gm, (list) => {
    const items = list.split("\n").map((l) => l.replace(/^- /, "").trim()).filter(Boolean);
    return "<ul class='list-disc list-inside space-y-1 my-4 text-[var(--fg-muted)]'>" +
      items.map((i) => `<li>${i}</li>`).join("") +
      "</ul>";
  });
  // Numbered lists
  html = html.replace(/(^\d+\. .*(?:\n\d+\. .*)*)/gm, (list) => {
    const items = list.split("\n").map((l) => l.replace(/^\d+\. /, "").trim()).filter(Boolean);
    return "<ol class='list-decimal list-inside space-y-1 my-4 text-[var(--fg-muted)]'>" +
      items.map((i) => `<li>${i}</li>`).join("") +
      "</ol>";
  });
  // Paragraphs — treat blocks of text separated by blank lines
  html = html
    .split(/\n{2,}/)
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("<")) return trimmed;
      return `<p class="text-[var(--fg-muted)] leading-relaxed my-4">${trimmed.replace(/\n/g, "<br />")}</p>`;
    })
    .join("\n");
  return html;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    http
      .get(`/blog/posts/${slug}`)
      .then(({ data }) => {
        if (alive) {
          setPost(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setNotFound(true);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  const html = useMemo(() => (post ? renderMarkdown(post.content_md) : ""), [post]);

  useSEO({
    title: post ? `${post.title} — PixelEmpire Blog` : "PixelEmpire Blog",
    description: post?.excerpt,
    path: `/blog/${slug}`,
    type: "article",
    image: post?.cover_image || undefined,
    jsonLd: post
      ? [
          orgSchema,
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            author: { "@type": "Organization", name: post.author || "PixelEmpire AI" },
            publisher: {
              "@type": "Organization",
              name: "PixelEmpire AI",
              logo: {
                "@type": "ImageObject",
                url: "https://pixel-empires.com/logo512.png",
              },
            },
            datePublished: post.published_at,
            dateModified: post.published_at,
            mainEntityOfPage: `https://pixel-empires.com/blog/${slug}`,
            image: post.cover_image || "https://pixel-empires.com/og-cover.png",
          },
        ]
      : null,
  });

  return (
    <PageShell testId="page-blog-post">
      <Link to="/blog" className="text-sm text-cyan-300 inline-flex items-center gap-1 hover:gap-2 transition-all">
        <ArrowLeft className="w-4 h-4" /> All articles
      </Link>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-[var(--fg-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : notFound ? (
        <div className="mt-10">
          <h1 className="font-display text-3xl text-white">Post not found</h1>
          <p className="mt-3 text-[var(--fg-muted)]">This article may have moved. Browse all articles instead.</p>
        </div>
      ) : post ? (
        <article className="mt-6" data-testid="article-body">
          <div className="text-xs font-mono text-[var(--fg-muted)] flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            {new Date(post.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            {post.tags?.length ? <span>· {post.tags.join(" · ")}</span> : null}
          </div>
          <h1
            className="mt-3 font-display text-3xl sm:text-5xl leading-tight text-white"
            data-testid="article-h1"
          >
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-4 text-lg text-[var(--fg-muted)] leading-relaxed">{post.excerpt}</p>
          )}
          <div className="mt-6" dangerouslySetInnerHTML={{ __html: html }} />

          <div className="mt-14 glass p-6 rounded-2xl text-center">
            <h2 className="font-display text-2xl text-white">Ready to claim your own block?</h2>
            <p className="mt-2 text-sm text-[var(--fg-muted)]">
              $0.50 per pixel · one-time · lifetime placement · do-follow SEO backlink.
            </p>
            <Link to="/" className="btn-primary mt-4 inline-flex" data-testid="article-cta">
              Open the grid →
            </Link>
          </div>
        </article>
      ) : null}
    </PageShell>
  );
}
